import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GitMerge, Search, AlertTriangle, ArrowRight, X, Users, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountChip = ({ acct, selected, onClick, keepLabel }) => (
  <button
    onClick={onClick}
    data-testid={`merge-acct-${acct.id}`}
    className={`text-left w-full p-3 rounded-lg border transition-colors ${selected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-blue-300 bg-white'}`}
  >
    <div className="flex items-center justify-between">
      <span className="font-semibold text-slate-900">{acct.full_name}</span>
      {keepLabel && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white">{keepLabel}</span>}
    </div>
    <div className="text-xs text-slate-500">{acct.email || '(no email)'} · {(acct.role || '').replace(/_/g, ' ')}</div>
    <div className="text-xs mt-1 flex gap-2">
      <span className="text-emerald-700 font-medium flex items-center gap-1"><ShieldCheck className="w-3 h-3" />{acct.cert_count} certs</span>
      {acct.is_active === false && <span className="text-red-500">inactive</span>}
      {acct.merged_into && <span className="text-amber-600">already merged</span>}
    </div>
  </button>
);

function MergeDialog({ accounts, onClose, onMerged }) {
  const [keepId, setKeepId] = useState(accounts[0]?.id);
  const [merging, setMerging] = useState(false);
  const keep = accounts.find(a => a.id === keepId);
  const dup = accounts.find(a => a.id !== keepId);

  const doMerge = async () => {
    if (!keep || !dup) return;
    if (!window.confirm(`Merge "${dup.full_name}" into "${keep.full_name}"?\n\n${dup.cert_count} certificate(s) will move to ${keep.full_name}, and ${dup.full_name}'s account will be deactivated. This cannot be auto-undone.`)) return;
    setMerging(true);
    try {
      const r = await axios.post(`${API}/admin/merge/accounts`, { keep_id: keep.id, duplicate_id: dup.id });
      toast.success(`Merged — ${r.data.certs_reassigned} cert(s) moved to ${r.data.kept}. ${r.data.deactivated} deactivated.`);
      onMerged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Merge failed'); }
    setMerging(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !merging && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="merge-dialog">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><GitMerge className="w-5 h-5 text-blue-600" /> Merge Accounts</h2>
          <button onClick={() => !merging && onClose()}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4">
          <p className="text-sm text-slate-600 mb-3">Choose the account to <b>keep</b>. The other's safety certificates move to it, then it is deactivated.</p>
          <div className="grid grid-cols-1 gap-2">
            {accounts.map(a => (
              <AccountChip key={a.id} acct={a} selected={a.id === keepId} keepLabel={a.id === keepId ? 'KEEP' : null} onClick={() => setKeepId(a.id)} />
            ))}
          </div>
          {keep && dup && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm bg-slate-50 rounded-lg p-3">
              <span className="font-medium text-red-600">{dup.full_name}</span>
              <ArrowRight className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-emerald-700">{keep.full_name}</span>
              <span className="text-slate-500">({dup.cert_count} certs move over)</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button onClick={() => !merging && onClose()} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={doMerge} disabled={merging || !dup} data-testid="merge-confirm-btn" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {merging ? 'Merging…' : 'Merge Accounts'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MergeAccounts() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogAccts, setDialogAccts] = useState(null);
  // manual merge
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [slotA, setSlotA] = useState(null);
  const [slotB, setSlotB] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/merge/candidates`); setGroups(r.data.groups || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load candidates'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const search = async (val) => {
    setQ(val);
    if (!val || val.length < 2) { setResults([]); return; }
    try { const r = await axios.get(`${API}/admin/merge/users`, { params: { q: val } }); setResults(r.data.users || []); }
    catch { setResults([]); }
  };

  const onMerged = () => { setDialogAccts(null); setSlotA(null); setSlotB(null); setResults([]); setQ(''); load(); };

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="merge-accounts-page">
      <div className="flex items-center gap-3 mb-1">
        <GitMerge className="w-7 h-7 text-slate-900" />
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Merge Duplicate Accounts</h1>
      </div>
      <p className="text-blue-800 text-sm mb-6">Consolidate duplicate employee accounts and move their safety certificates onto one record.</p>

      {/* Suggested duplicates */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Suggested Duplicates</h2>
        {loading ? <div className="text-slate-500 text-center py-8">Loading…</div> :
          groups.length === 0 ? <div className="text-slate-500 text-center py-8" data-testid="merge-no-candidates">No likely duplicate accounts found.</div> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groups.map((g, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`merge-group-${g.last_name}`}>
                  <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> Possible match — {g.last_name}</div>
                  <div className="space-y-1.5">
                    {g.accounts.map(a => (
                      <div key={a.id} className="text-sm">
                        <span className="font-medium text-slate-900">{a.full_name}</span>
                        <span className="text-slate-500"> · {a.email || '(no email)'} · {a.cert_count} certs</span>
                        {a.is_active === false && <span className="text-red-500 text-xs"> (inactive)</span>}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setDialogAccts(g.accounts)} data-testid={`merge-group-btn-${g.last_name}`}
                    className="mt-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium flex items-center gap-1">
                    <GitMerge className="w-3.5 h-3.5" /> Review &amp; Merge
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Manual merge */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Manual Merge</h2>
        <p className="text-xs text-slate-500 mb-3">Pick two accounts to merge (in case they weren't auto-detected).</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          {[{ slot: slotA, set: setSlotA, label: 'Account 1' }, { slot: slotB, set: setSlotB, label: 'Account 2' }].map((s, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-3 min-h-[64px]">
              <div className="text-xs font-medium text-slate-500 mb-1">{s.label}</div>
              {s.slot ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{s.slot.full_name}</div>
                    <div className="text-xs text-slate-500">{s.slot.email || '(no email)'} · {s.slot.cert_count} certs</div>
                  </div>
                  <button onClick={() => s.set(null)}><X className="w-4 h-4 text-slate-500" /></button>
                </div>
              ) : <div className="text-xs text-slate-500">Search below and pick an account…</div>}
            </div>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input value={q} onChange={e => search(e.target.value)} placeholder="Search by name or email"
            data-testid="merge-search" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500" />
          {results.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {results.map(u => (
                <button key={u.id} onClick={() => { if (!slotA) setSlotA(u); else setSlotB(u); setResults([]); setQ(''); }}
                  data-testid={`merge-result-${u.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0">
                  <span className="font-medium text-slate-900">{u.full_name}</span>
                  <span className="text-slate-500 text-xs"> · {u.email || '(no email)'} · {u.cert_count} certs</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => setDialogAccts([slotA, slotB])}
          disabled={!slotA || !slotB || slotA.id === slotB.id}
          data-testid="merge-manual-review"
          className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          Review &amp; Merge
        </button>
      </div>

      {dialogAccts && <MergeDialog accounts={dialogAccts.filter(Boolean)} onClose={() => setDialogAccts(null)} onMerged={onMerged} />}
    </div>
  );
}
